/**
 * useLocalizedProfileMetadata Hook
 *
 * Resolves a profile's localized content via the resolve_profiles_for_listing
 * RPC (SECURITY DEFINER, server-side locale resolution + cascade).
 *
 * MIGRATION (2026-06-07, profile localization pass Brief 4): previously this
 * hook did a strict-locale read of localization_metadata and silently fell
 * back to the EN bio when the row was missing or its body was NULL — which is
 * how thousands of fully-translated profiles rendered the wrong locale. The
 * RPC serves the cascade decision centrally and returns `display_eligible`.
 *
 * THE CONTRACT:
 *   display_eligible → what to render; no_index → robots meta ONLY;
 *   needs_review → optional badge, never blocks display.
 *
 * Used for:
 * - Expert profile pages (bio display)
 * - Author sections in publication detail
 *
 * @example
 * ```tsx
 * const { localizedBio, isLoading } = useLocalizedProfileMetadata(profileId);
 * const displayBio = localizedBio ?? originalBio;
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { usePrimaryLocale } from './usePrimaryLocale';
import { useLocale } from '@/contexts/LocaleContext';
import { fetchResolvedProfilesForListing, resolveDisplayBody } from '@/lib/profileResolver';
import type { ListingProfile } from '@/lib/profileResolver';

interface LocalizedProfileMetadataResult {
  /** The body to render per the display_eligible contract (localized, or EN safety fallback) */
  localizedBio: string | null;
  /** Pre-translated display name — null by contract (names don't translate) */
  localizedName: string | null;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether localization is needed for current locale */
  needsLocalization: boolean;
  /** Locale actually served (differs from requested when cascade fired) */
  localeServed: string | null;
  /** Locale that was requested */
  requestedLocale: string | null;
  /** Robots meta ONLY — never use as a render gate */
  noIndex: boolean;
  /** Translation may be updating (optional badge — does not block display) */
  needsReview: boolean;
  /** Localized SEO fields for head tags */
  metaDescription: string | null;
  aioSnippet: string | null;
  schemaAbout: string | null;
  /** Always "/{vanity_url}" — enforce on the page, never UUID */
  canonicalPath: string | null;
}

/**
 * Shared query for a single profile's resolved content. The query key is
 * shared with useProfileSEOMetadata so the RPC fires once per (profile, locale).
 */
export function useResolvedProfileListing(profileId: string | undefined) {
  const { settings } = useLocale();
  const { resolveToPrimary } = usePrimaryLocale();

  const contentLocale = resolveToPrimary(settings.locale);

  const query = useQuery({
    queryKey: ['resolved-profile-listing', profileId, contentLocale],
    queryFn: async (): Promise<ListingProfile | null> => {
      if (!profileId) return null;
      const profiles = await fetchResolvedProfilesForListing([profileId], contentLocale);
      return profiles[0] ?? null;
    },
    enabled: !!profileId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  return { ...query, contentLocale };
}

/**
 * Hook to fetch a profile's localized content via the resolver RPC.
 *
 * @param profileId - The user profile ID to fetch metadata for
 * @returns Object with localized bio/name, contract fields, and loading state
 */
export function useLocalizedProfileMetadata(profileId: string | undefined): LocalizedProfileMetadataResult {
  const { data, isLoading, contentLocale } = useResolvedProfileListing(profileId);
  const needsLocalization = contentLocale !== 'en';

  return {
    localizedBio: data ? resolveDisplayBody(data) : null,
    // Profile names are SQL-sourced invariants (copied from users.display_name)
    localizedName: data?.localized.translated_name ?? null,
    isLoading,
    needsLocalization,
    localeServed: data?.localized.locale_served ?? null,
    requestedLocale: data?.localized.requested_locale ?? null,
    noIndex: data?.localized.no_index ?? false,
    needsReview: data?.localized.needs_review ?? false,
    metaDescription: data?.localized.meta_description ?? null,
    aioSnippet: data?.localized.aio_snippet ?? null,
    schemaAbout: data?.localized.schema_about ?? null,
    canonicalPath: data?.canonical_path ?? null,
  };
}
